import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Wallet as WalletIcon, FolderKanban, CheckCircle2 } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatTile from "../../components/StatTile";
import { listMyProjects, getWallet } from "../../services/archMarketplace";

const ACTIVE = ["ROUTING", "ASSIGNED", "IN_PROGRESS", "IN_REVIEW", "CHANGES_REQUESTED"];

const STATUS_PILL = {
  DRAFT: "bg-gray-100 text-gray-600",
  PRICED: "bg-indigo-100 text-indigo-700",
  APPROVED: "bg-indigo-100 text-indigo-700",
  ROUTING: "bg-amber-100 text-amber-700",
  ASSIGNED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-blue-100 text-blue-700",
  IN_REVIEW: "bg-purple-100 text-purple-700",
  CHANGES_REQUESTED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  NO_CANDIDATES: "bg-rose-100 text-rose-700",
  CANCELLED: "bg-gray-100 text-gray-500",
};

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const humanize = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function ArchitectDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [p, w] = await Promise.all([listMyProjects(), getWallet().catch(() => null)]);
        if (!mounted) return;
        setProjects(Array.isArray(p) ? p : []);
        setBalance(w?.balance ?? null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const activeCount = projects.filter((p) => ACTIVE.includes(p.status)).length;
  const doneCount = projects.filter((p) => p.status === "COMPLETED").length;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="My Projects"
        subtitle="Post work, get instant pricing, and get matched to vetted talent"
        icon={Building2}
        actions={
          <button
            onClick={() => navigate("/architect/projects/new")}
            className="inline-flex items-center gap-2 rounded-md bg-white/95 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-white"
          >
            <Plus size={16} /> New Project
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile icon={FolderKanban} label="Total Projects" value={projects.length} accent="slate" loading={loading} />
        <StatTile icon={Building2} label="Active" value={activeCount} accent="amber" loading={loading} />
        <StatTile icon={CheckCircle2} label="Completed" value={doneCount} accent="emerald" loading={loading} />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Projects</h3>
        <button
          onClick={() => navigate("/architect/wallet")}
          className="inline-flex items-center gap-2 text-sm font-medium text-flamingo-700 hover:text-flamingo-800"
        >
          <WalletIcon size={16} /> Wallet: {fmtINR(balance)}
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : projects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
          <p className="text-gray-500 mb-4">No projects yet.</p>
          <button
            onClick={() => navigate("/architect/projects/new")}
            className="inline-flex items-center gap-2 rounded-md bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700"
          >
            <Plus size={16} /> Post your first project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <button
              key={p.archProjectId}
              onClick={() => navigate(`/architect/projects/${p.archProjectId}`)}
              className="text-left rounded-lg border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-gray-800 line-clamp-1">{p.projectName}</h4>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[p.status] || "bg-gray-100 text-gray-600"}`}>
                  {humanize(p.status)}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500 line-clamp-2">{p.description}</p>
              <div className="mt-3 text-sm font-semibold text-gray-700">
                {fmtINR(p.approvedPrice ?? p.estimatedPrice)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ArchitectDashboard;
